import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import decodeToken from '../../../../../utils/decode-token';
import {
  deletePendingTranslationFile,
  downloadTranslationFileFromPre,
  fetchTranslationFiles,
  publishTranslationFileToPre,
  publishTranslationFileToPro,
  uploadTranslationFileToPre,
  fetchPreviewToken
} from '../../../../../api/translatorLibraryApi';
import {
  extractFileName,
  getCountryCode,
  getCountryOptions,
  getLanguageOptions,
  MASTER_COUNTRY,
  mapTranslationFile,
  shouldShowTranslationUpdate,
} from '../model/library-mapper';
import getTranslationApiErrorMessage from '../../../../../utils/translation-api-error';
import getTranslationLanguageLabel from '../../../../../utils/translation-language-label';

const INITIAL_FORM = {
  file: null,
  country: '',
  language: '',
  type: '',
};

const TYPE_OPTIONS = [
  { value: 'App', labelKey: 'library-type-application' },
  { value: 'Exp', labelKey: 'library-type-experiences' },
  //{ value: 'Survey', labelKey: 'library-type-survey' },
  { value: 'Legal', labelKey: 'library-type-legal' },
  { value: 'Product', labelKey: 'library-type-product' },
];

const MASTER_HIDDEN_ROLES = ['marketAdmin', 'countryAdmin'];

const parseLoggedUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem('loggedUser') || '{}');
  } catch {
    return {};
  }
};

const isXlsxFile = (file) =>
  Boolean(file && typeof file.name === 'string' && file.name.toLowerCase().endsWith('.xlsx'));

const sortByNewest = (rows) => {
  const toTs = (value) => {
    if (!value) {
      return 0;
    }

    const ts = Date.parse(value);
    return Number.isFinite(ts) ? ts : 0;
  };

  return [...rows].sort((a, b) => toTs(b.uploadedAt) - toTs(a.uploadedAt));
};

const useLibraryController = ({ canPublishPro = false } = {}) => {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = token ? decodeToken(token).role : '';
  const [loggedUser, setLoggedUser] = useState(parseLoggedUser);

  const [activeTab, setActiveTab] = useState('translations');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [formError, setFormError] = useState('');

  const [pendingProFile, setPendingProFile] = useState(null);
  const [isPublishingPro, setIsPublishingPro] = useState(false);
  const [deployProError, setDeployProError] = useState('');
  const [pendingDeleteFile, setPendingDeleteFile] = useState(null);

  const loadFiles = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await fetchTranslationFiles();
      const mappedRows = (response.data || [])
        .map((rawFile) => {
          const file = mapTranslationFile(rawFile);

          return {
            ...file,
            showUpdateIndicator: shouldShowTranslationUpdate(file, role),
          };
        })
        .filter((file) => !MASTER_HIDDEN_ROLES.includes(role) || Boolean(file.country));
      setRows(sortByNewest(mappedRows));
    } catch (error) {
      setErrorMessage(getTranslationApiErrorMessage(error, t, 'library-feedback-load-error'));
    } finally {
      setIsLoading(false);
    }
  }, [role, t]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    const handleLoggedUserUpdated = (event) => {
      setLoggedUser(event.detail || parseLoggedUser());
    };

    window.addEventListener('hac:logged-user-updated', handleLoggedUserUpdated);
    return () => {
      window.removeEventListener(
        'hac:logged-user-updated',
        handleLoggedUserUpdated
      );
    };
  }, []);

  const MASTER_FILTER_VALUE = '__master__';

  const countryFilterOptions = useMemo(() => {
    const countries = [...new Set(rows.map((row) => row.country).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({ value: country, label: country }));
    if (rows.some((row) => !row.country)) {
      countries.unshift({ value: MASTER_FILTER_VALUE, label: t('library-master-country') });
    }
    return countries;
  }, [rows, t]);

  const filteredRows = useMemo(() => {
    const criteria = search.trim().toLowerCase();
    return rows.filter((row) => {
      const searchable = [
        row.fileName,
        row.country,
        row.language,
        row.type,
        row.status,
      ]
        .join(' ')
        .toLowerCase();

      return row.type !== 'Survey' &&
        (!criteria || searchable.includes(criteria)) &&
        (!statusFilter || row.status === statusFilter) &&
        (!countryFilter || (countryFilter === MASTER_FILTER_VALUE ? !row.country : row.country === countryFilter)) &&
        (!typeFilter || row.type === typeFilter);
    });
  }, [rows, search, statusFilter, countryFilter, typeFilter]);

  const countryOptions = useMemo(
    () => [...getCountryOptions(loggedUser, rows, role)].sort((a, b) => {
      const labelA = t(`countries-${getCountryCode(a)}`, { defaultValue: a });
      const labelB = t(`countries-${getCountryCode(b)}`, { defaultValue: b });
      return labelA.localeCompare(labelB);
    }),
    [loggedUser, role, rows, t]
  );

  const languageOptions = useMemo(
    () => [...getLanguageOptions(loggedUser, rows, form.country, role, form.type)].sort((a, b) => {
      const labelA = getTranslationLanguageLabel(a, getCountryCode(form.country), t);
      const labelB = getTranslationLanguageLabel(b, getCountryCode(form.country), t);
      return labelA.localeCompare(labelB);
    }),
    [form.country, form.type, loggedUser, role, rows, t]
  );

  useEffect(() => {
    if (role === 'admin' || countryOptions.length !== 1) return;

    const [onlyCountry] = countryOptions;
    setForm((current) => (
      current.country === onlyCountry
        ? current
        : { ...current, country: onlyCountry, language: '' }
    ));
  }, [countryOptions, form.country, role]);

  useEffect(() => {
    if (languageOptions.length !== 1) return;

    const [onlyLanguage] = languageOptions;
    setForm((current) => (
      current.language === onlyLanguage
        ? current
        : { ...current, language: onlyLanguage }
    ));
  }, [form.language, languageOptions]);

  const resetAddForm = () => {
    setForm(INITIAL_FORM);
    setFormError('');
  };

  const openAddModal = () => {
    resetAddForm();
    setSuccessMessage('');
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    resetAddForm();
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      ...(field === 'country' ? { language: '' } : {}),
      [field]: value,
    }));
  };

  const isMasterEnglishUpload =
    role === 'admin' && form.country === MASTER_COUNTRY && form.language === 'en';

  const handleUpload = async () => {
    setFormError('');
    setErrorMessage('');
    setSuccessMessage('');

    if (!isXlsxFile(form.file)) {
      setFormError(t('library-upload-file-format-error'));
      return;
    }

    if ((!form.country && !isMasterEnglishUpload) || !form.language || !form.type) {
      setFormError(t('library-upload-required-fields-error'));
      return;
    }

    setIsSubmitting(true);

    try {
      await uploadTranslationFileToPre({
        file: form.file,
        country: form.country,
        language: form.language,
        type: form.type,
      });
      setSuccessMessage(t('library-feedback-upload-success'));
      closeAddModal();
      await loadFiles();
    } catch (error) {
      setFormError(getTranslationApiErrorMessage(error, t, 'library-feedback-upload-error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (row) => {
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const fileName = row.fileName || extractFileName(row.excelPath);
      const response = await downloadTranslationFileFromPre(fileName);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName || 'translation.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(getTranslationApiErrorMessage(error, t, 'library-feedback-download-error'));
    }
  };

  const handlePreviewAs = async (row, role) => {
  try {
    const response = await fetchPreviewToken(role, row.language, row.country);
    const token = response.data?.token;
    if (token) {
      window.open(`/auto-login?token=${token}`, '_blank');
    }
  } catch (error) {
    setErrorMessage(getTranslationApiErrorMessage(error, t, 'library-feedback-preview-error'));
  }
};


  const deployToPre = async (fileId) => {
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await publishTranslationFileToPre(fileId);
      setSuccessMessage(t('library-feedback-pre-success'));
      await loadFiles();
    } catch (error) {
      setErrorMessage(getTranslationApiErrorMessage(error, t, 'library-feedback-pre-error'));
    }
  };

  const requestDeployToPro = (row) => {
    if (!canPublishPro) return;
    setDeployProError('');
    setPendingProFile(row);
  };

  const cancelDeployToPro = () => {
    if (isPublishingPro) return;
    setDeployProError('');
    setPendingProFile(null);
  };

  const confirmDeployToPro = async (password) => {
    if (!canPublishPro) {
      setPendingProFile(null);
      return;
    }

    if (!pendingProFile?.id) {
      setPendingProFile(null);
      return;
    }

    if (!password) {
      setDeployProError(t('library-confirm-pro-password-required'));
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    setDeployProError('');
    setIsPublishingPro(true);

    try {
      await publishTranslationFileToPro(pendingProFile.id, password);
      setSuccessMessage(t('library-feedback-pro-success'));
      setPendingProFile(null);
      await loadFiles();
    } catch {
      setDeployProError(t('library-feedback-pro-invalid-password'));
    } finally {
      setIsPublishingPro(false);
    }
  };

  const requestDeleteFile = (row) => {
    if (row.status === 'pending') setPendingDeleteFile(row);
  };

  const cancelDeleteFile = () => setPendingDeleteFile(null);

  const confirmDeleteFile = async () => {
    if (!pendingDeleteFile?.id) {
      setPendingDeleteFile(null);
      return;
    }

    setErrorMessage('');
    setSuccessMessage('');
    try {
      await deletePendingTranslationFile(pendingDeleteFile.id);
      setPendingDeleteFile(null);
      setSuccessMessage(t('library-feedback-delete-success'));
      await loadFiles();
    } catch (error) {
      setErrorMessage(getTranslationApiErrorMessage(error, t, 'library-feedback-delete-error'));
    }
  };

  const isUploadDisabled =
    isSubmitting ||
    !form.file ||
    !isXlsxFile(form.file) ||
    !form.country ||
    !form.language ||
    !form.type;

  return {
    activeTab,
    setActiveTab,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    countryFilter,
    setCountryFilter,
    countryFilterOptions,
    typeFilter,
    setTypeFilter,
    rows: filteredRows,
    isLoading,
    errorMessage,
    successMessage,
    clearErrorMessage: () => setErrorMessage(''),
    clearSuccessMessage: () => setSuccessMessage(''),
    countryOptions,
    languageOptions,
    typeOptions: TYPE_OPTIONS,
    isAddModalOpen,
    openAddModal,
    closeAddModal,
    form,
    formError,
    isSubmitting,
    isUploadDisabled,
    updateFormField,
    handleUpload,
    deployToPre,
    requestDeployToPro,
    pendingProFile,
    isPublishingPro,
    deployProError,
    clearDeployProError: () => setDeployProError(''),
    cancelDeployToPro,
    confirmDeployToPro,
    pendingDeleteFile,
    requestDeleteFile,
    cancelDeleteFile,
    confirmDeleteFile,
    handleDownload,
    handlePreviewAs
  };
};

export default useLibraryController;
